import { MathJaxContext } from "better-react-mathjax";
import { isBefore, subHours } from "date-fns";
import { useContext, useEffect, useMemo } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { pdfjs } from "react-pdf";
import { useLocation, useParams } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

import { Classroom } from "./classroom";
import { OverlayError } from "./overlay_error";

import { AvContextProvider } from "components/av/av_provider";
import { FORUM_URL, INTERCOM_APP_ID, INTERCOM_ENABLED } from "config";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPI, ForumAPIContext } from "contexts/forum_api_context";
import { HardwareMonitor, HardwareMonitorContext } from "contexts/hardware_monitor_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsAlreadyPresent } from "hooks/use_is_already_present";
import { useUserProperties } from "hooks/use_user_properties";
import { sendPubsubMessage } from "shared/reducers/classroom";
import { PageUnloadReasonType, PubsubErrorCodes, PubsubMessageTypeType } from "shared/types";
import { RoleType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { setCurrentUserId } from "store/reducers/local";
import { redirectToClassEditPage, redirectToHome } from "utils/classroom";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

function ClassroomWrapper({ userId }: { userId: string }) {
  const dispatch = useAppDispatch();
  const { classId } = useParams();
  const isAlreadyPresent = useIsAlreadyPresent();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUser = useUserProperties(userId, ["id"]);
  const isLoading = useAppSelector(classroomSelectors.selectLoading());
  const overlayError = useAppSelector(localSelectors.selectOverlayError());
  const pubsubErrorCode = useAppSelector(localSelectors.selectPubsubErrorCode());
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const currentUserIsRecordingAgent = useAppSelector(
    classroomSelectors.selectCurrentUserIsRecordingAgent(),
  );
  const recordingIsPreparing = useAppSelector(classroomSelectors.selectRecordingIsPreparing());
  const { page_title, favicon_url } = classInfo.branding || {};
  const { disable_slideshow_recording, is_in_flight, is_open } = classInfo;
  const { canEnterClassroom, canParticipateInClass } = useCapabilities();
  const role = useAppSelector(classroomSelectors.selectRoleForCurrentUser());
  const location = useLocation();
  const wantsToObserve = location.pathname.split("/").includes("observe");

  const forumAPI = useMemo(() => new ForumAPI(dispatch, userId), [dispatch, userId]);
  const hardwareMonitor = useMemo(() => new HardwareMonitor(dispatch, userId), [dispatch, userId]);

  const flightAPI = useContext(FlightAPIContext);
  const isTooEarlyToEnterClassroom = useMemo(() => {
    if (!classInfo || !classInfo.date) return false;
    const startTime = new Date(classInfo.date);
    const currentTime = new Date(flightAPI.getServerMilliseconds());
    return isBefore(currentTime, subHours(startTime, 1));
  }, [classInfo, flightAPI]);

  useEffect(() => {
    if (isLoading || !classId) return;
    if ((!currentUser || !canEnterClassroom) && !is_open) {
      window.flightLogger.warn(
        `[ClassroomWrapper] User ${userId} is not allowed to enter the classroom.`,
      );
      redirectToHome();
    } else if (
      is_open &&
      !currentUserIsRecordingAgent &&
      (!canEnterClassroom || (role === RoleType.None && !wantsToObserve))
    ) {
      forumAPI.enrollInOpenClass(classId);
    } else {
      window.flightLogger.log(`[ClassroomWrapper] User ${userId} - Starting hardware monitoring.`);
      hardwareMonitor.maybeStartMonitoringHardware();
    }
  }, [
    canEnterClassroom,
    classId,
    forumAPI,
    isLoading,
    is_open,
    userId,
    hardwareMonitor,
    role,
    wantsToObserve,
    currentUserIsRecordingAgent,
    currentUser,
  ]);

  useEffect(() => {
    if (isLoading) return;
    if (canParticipateInClass && isTooEarlyToEnterClassroom) {
      const courseId = classInfo.course.id;
      const sectionId = classInfo.section.id;
      redirectToClassEditPage(
        { courseId, sectionId, classId: classInfo.id },
        PageUnloadReasonType.TOO_EARLY_TO_ENTER_CLASSROOM,
      );
    }
  }, [canParticipateInClass, classInfo, isLoading, isTooEarlyToEnterClassroom]);

  useEffect(() => {
    if (isAlreadyPresent) {
      window.flightLogger.log(
        "[App] User is already present in this class in another tab or window. Let's close the other instance.",
      );
      dispatch(
        sendPubsubMessage({
          type: PubsubMessageTypeType.KICK_USER_OUT_WITHOUT_WARNING,
          data: {
            targetUserId: userId,
            reason: PageUnloadReasonType.KICKED_OUT_OF_CLASS_NEW_TAB,
          },
        }),
      );
    }
  }, [dispatch, userId, isAlreadyPresent]);

  useEffect(() => {
    if (!is_in_flight) {
      if (!currentUserIsRecordingAgent || !recordingIsPreparing) {
        const classUrl = `${FORUM_URL}/app/classes/${classId}`;
        const disableSlideshowMode = disable_slideshow_recording ? "&disable-slideshow-mode=1" : "";
        const urlParams = new URLSearchParams(window.location.search);
        const tryingToPresent = urlParams.has("presentation");

        let forumUrl = classUrl;
        if (currentUserIsRecordingAgent) {
          forumUrl = `${classUrl}/observe?recorder&skip-av-check=1${disableSlideshowMode}`;
        } else if (tryingToPresent) {
          forumUrl = `${classUrl}?presentation=1`;
        }
        window.location.replace(forumUrl);
      }
    }
  }, [
    disable_slideshow_recording,
    is_in_flight,
    classId,
    currentUserIsRecordingAgent,
    recordingIsPreparing,
  ]);

  useEffect(() => {
    if (currentUserIsRecordingAgent) {
      sessionStorage.setItem("enableForumLogs", "true");
    }
  }, [currentUserIsRecordingAgent]);

  useEffect(() => {
    dispatch(setCurrentUserId({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    const title = `Forum | ${page_title || "Minerva Project"}`;
    document.title = title;
    const meta = document.querySelector<HTMLMetaElement>("meta[name='description']");
    if (meta) {
      meta.content = title;
    }
  }, [page_title]);

  useEffect(() => {
    if (favicon_url) {
      const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (link) {
        link.href = favicon_url;
      }
      const link2 = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
      if (link2) {
        link2.href = favicon_url;
      }
    }
  }, [favicon_url]);

  useEffect(() => {
    if (!classId) return;
    if (pubsubErrorCode === PubsubErrorCodes.CLASS_NOT_IN_FLIGHT) {
      redirectToClassEditPage({ classId }, PageUnloadReasonType.CLASS_NOT_IN_FLIGHT);
    } else if (pubsubErrorCode === PubsubErrorCodes.CLASS_EXPIRED) {
      redirectToClassEditPage({ classId }, PageUnloadReasonType.CLASS_EXPIRED);
    }
  }, [classId, pubsubErrorCode]);

  if (isLoading || !userId || !currentUserId || !canEnterClassroom) return null;

  return (
    <ForumAPIContext.Provider value={forumAPI}>
      {/* Note that we only want to load the providers once the classroom store data is loaded, //
      otherwise side effects will be triggered multiple times. */}
      <HardwareMonitorContext.Provider value={hardwareMonitor}>
        <AvContextProvider userId={userId}>
          <DndProvider backend={HTML5Backend}>
            <IntercomProvider
              appId={INTERCOM_APP_ID || ""}
              shouldInitialize={INTERCOM_ENABLED}
              autoBoot
            >
              <MathJaxContext
                version={2}
                config={{
                  CommonHTML: {
                    linebreaks: {
                      automatic: true,
                    },
                  },
                }}
              >
                <Classroom userId={userId} />
                {overlayError && <OverlayError text={overlayError} />}
              </MathJaxContext>
            </IntercomProvider>
          </DndProvider>
        </AvContextProvider>
      </HardwareMonitorContext.Provider>
    </ForumAPIContext.Provider>
  );
}

export { ClassroomWrapper };
